import { CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { NavbarService } from './Services/navbar.service';
import { UserAccessService } from './Services/UserAccess/user-access.service';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export const authGuardGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const navbarService = inject(NavbarService); // kept intentionally
  const userAccessService = inject(UserAccessService);

  // ✅ Token check
  const token = sessionStorage.getItem('token');
  if (!token) {
    router.navigate(['/login']);
    return false;
  }

  try {
    const decodedToken: any = jwtDecode(token);

    const userRole = decodedToken?.role;
    const userSubRole = decodedToken?.sub_role;
    const currentUserId = decodedToken?.sub;

    const requiredRole = route.data?.['role'] as string;
    const requiredSubRole = route.data?.['sub_role'] as string;

    // ✅ Role check
    if (requiredRole && userRole !== requiredRole) {
      router.navigate(['/error-403']);
      return false;
    }

    // ✅ Sub-role check
    if (requiredSubRole && userSubRole !== requiredSubRole) {
      router.navigate(['/error-403']);
      return false;
    }

    // ✅ Token expiry check
    const now = Math.floor(Date.now() / 1000);
    if (decodedToken.exp && decodedToken.exp < now) {
      sessionStorage.clear();
      router.navigate(['/login']);
      return false;
    }

    // 🔐 Staff permission logic (NON-Karigar)
    if (
      userRole === 'Staff' &&
      userSubRole !== 'Karigar' &&
      userSubRole !== 'Customer'
    ) {
      const currentUrl = state.url.split('?')[0];

      // ✅ Avoid infinite redirect and allow default staff dashboard paths
      if (currentUrl === '/error-403' || currentUrl === '/login' || currentUrl === '/rm-portal/home' || currentUrl === '/rm-portal' || currentUrl === '/rm-portal/assets') {
        return true;
      }

      return userAccessService.getUserAccess(currentUserId).pipe(
        map((response: any) => {
          const data = response?.data;

          if (!data) {
            router.navigate(['/error-403']);
            return false;
          }

          // ✅ Allowed routes from DB
          const allowedRoutes: string[] = [
            ...(data.mainmenu_visibility || []),
            ...(data.submenu_visibility || [])
          ].map((r: string) => (r.startsWith('/') ? r : '/' + r));

          // Sell always opens /payment-details after items. Allow bill only if this staff already has sell.
          // Does not apply to GST/CA/Karigar menus (those routes are not /sell-details-lot).
          if (allowedRoutes.includes('/sell-details-lot') && !allowedRoutes.includes('/payment-details')) {
            allowedRoutes.push('/payment-details');
          }

          // ✅ Normalize URL ONLY if LAST segment is numeric ID
          const normalizeUrl = (url: string): string => {
            return /\/\d+$/.test(url) ? url.replace(/\/\d+$/, '') : url;
          };

          const normalizedUrl = normalizeUrl(currentUrl);
          //this is for admin panel route for submenus if you want the another headers main maeus also do same functionality add here the route 
          // code added by Pratik @ 18/06/2026
          const mainMenusWithSubmenus = [
            '/admin-panel'
          ];

          const hasAccess = allowedRoutes.some(route => {
            if (normalizedUrl === route) {
              return true;
            }
            if (mainMenusWithSubmenus.includes(route)) {
              return false;
            }
            return normalizedUrl.startsWith(route + '/');
          });

          if (!hasAccess) {
            console.error('[AuthGuard] Access Denied for URL:', normalizedUrl);
            router.navigate(['/error-403']);
            return false;
          }

          return true;
        }),
        catchError(() => {
          router.navigate(['/error-403']);
          return of(false);
        })
      );
    }

    // ✅ Allow other roles
    return true;

  } catch (error) {
    router.navigate(['/login']);
    return false;
  }
};

export const kalakarGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);

  const token = sessionStorage.getItem('token');
  if (!token) {
    router.navigate(['/login']);
    return false;
  }

  try {
    const decoded: any = jwtDecode(token);

    const role = decoded?.role;
    const subRole = decoded?.sub_role;

    // ✅ ONLY Staff + Karigar allowed , code update by Pratik for the staff also handdle the Karigar portal if they have the access
    if (role !== 'Staff' || subRole !== 'Karigar') {
      if (role === 'Staff' && subRole !== 'Karigar') {
        const supplierId = localStorage.getItem('supplierId');
        if (supplierId) {
          router.navigate(['/admin-panel/admin-kalakar'], { queryParams: { id: supplierId } });
          return false;
        }
      }
      router.navigate(['/error-403']);
      return false;
    }

    const allowedRoutes = [
      '/kalakaar-management/home',
      '/kalakaar-management/order',
      '/kalakaar-management/raw-material',
      '/kalakaar-management/account',
    ];

    const currentUrl = state.url.split('?')[0];

    // ✅ Allow ID based routes like /order/10/edit
    if (/\/\d+(\/|$)/.test(currentUrl)) {
      return true;
    }

    // ✅ Check access
    if (!allowedRoutes.includes(currentUrl)) {
      router.navigate(['/error-403']);
      return false;
    }

    return true;
  } catch {
    router.navigate(['/login']);
    return false;
  }
};

export const CAGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const userAccessService = inject(UserAccessService);

  const token = sessionStorage.getItem('token');
  if (!token) {
    router.navigate(['/login']);
    return false;
  }

  try {
    const decoded: any = jwtDecode(token);

    const role = decoded?.role;
    const subRole = decoded?.sub_role;
    const currentUserId = decoded?.sub;

    // ✅ Only Staff + Chartered Accountant
    if (role !== 'Staff' || subRole !== 'Chartered Accountant') {
      router.navigate(['/error-403']);
      return false;
    }

    // ✅ Token expiry check
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp < now) {
      sessionStorage.clear();
      router.navigate(['/login']);
      return false;
    }

    const currentUrl = state.url.split('?')[0];

    // ✅ Avoid infinite redirect
    if (currentUrl === '/error-403' || currentUrl === '/login') {
      return true;
    }

    // ✅ DB-based permission check (same as authGuardGuard for regular staff)
    return userAccessService.getUserAccess(currentUserId).pipe(
      map((response: any) => {
        const data = response?.data;

        if (!data) {
          router.navigate(['/error-403']);
          return false;
        }

        // ✅ Allowed routes from DB
        const allowedRoutes: string[] = [
          ...(data.mainmenu_visibility || []),
          ...(data.submenu_visibility || [])
        ].map((r: string) => (r.startsWith('/') ? r : '/' + r));

        // ✅ Normalize URL — strip trailing numeric ID
        const normalizeUrl = (url: string): string => {
          return /\/\d+$/.test(url) ? url.replace(/\/\d+$/, '') : url;
        };

        const normalizedUrl = normalizeUrl(currentUrl);
        //this is for admin panel route for submenus if you want the another headers main maeus also do same functionality add here the route 
        // code added by Pratik @ 18/06/2026
        const mainMenusWithSubmenus = [
          '/admin-panel'
        ];

        const hasAccess = allowedRoutes.some(allowedRoute => {
          if (normalizedUrl === allowedRoute) return true;
          if (mainMenusWithSubmenus.includes(allowedRoute)) return false;
          return normalizedUrl.startsWith(allowedRoute + '/');
        });

        if (!hasAccess) {
          console.error('[CAGuard] Access Denied for URL:', normalizedUrl);
          router.navigate(['/error-403']);
          return false;
        }

        return true;
      }),
      catchError(() => {
        router.navigate(['/error-403']);
        return of(false);
      })
    );

  } catch {
    router.navigate(['/login']);
    return false;
  }
};